import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import Layout from './components/Layout/Layout';
import Login from './pages/Login';
import Register from './pages/Register';
import RegisterComplete from './pages/RegisterComplete';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import AdminDashboard from './pages/AdminDashboard';
import Beneficiaries from './pages/Beneficiaries';
import Subscriptions from './pages/Subscriptions';
import SubscriptionDetail from './pages/SubscriptionDetail';
import AdminSubscriptions from './pages/AdminSubscriptions';
import AdminPolicies from './pages/AdminPolicies';
import AdminRequests from './pages/AdminRequests';
import AdminRequestTypes from './pages/AdminRequestTypes';
import AdminReasons from './pages/AdminReasons';
import MyPolicies from './pages/MyPolicies';
import MyRequests from './pages/MyRequests';
import Contributions from './pages/Contributions';
import Offers from './pages/Offers';
import Profile from './pages/Profile';
import Users from './pages/Users';
import AdminParametrage from './pages/AdminParametrage';
import AdminDataMigration from './pages/AdminDataMigration';
import AdminSituationMatrimoniale from './pages/AdminSituationMatrimoniale';
import AdminLiensParente from './pages/AdminLiensParente';
import AdminFonctions from './pages/AdminFonctions';
import AdminStatuts from './pages/AdminStatuts';
import AdminEnterprise from './pages/AdminEnterprise';
import AdminMutualistes from './pages/AdminMutualistes';
import AdminMutualistesList from './pages/AdminMutualistesList';
import AdminMutualisteCreate from './pages/AdminMutualisteCreate';
import AdminMutualisteDetail from './pages/AdminMutualisteDetail';
import AdminMutualisteEdit from './pages/AdminMutualisteEdit';
import AdminCronJobs from './pages/AdminCronJobs';
import LoadingSpinner from './components/UI/LoadingSpinner';

// Pages de paiement
import AdminPayments from './pages/AdminPayments';
import AdminPaymentsMutualistes from './pages/AdminPaymentsMutualistes';
import AdminPaymentsEntreprises from './pages/AdminPaymentsEntreprises';
import PaymentSelfService from './pages/payments/PaymentSelfService';
import PaymentThirdParty from './pages/payments/PaymentThirdParty';
import PaymentBulk from './pages/payments/PaymentBulk';
import PaymentHistory from './pages/payments/PaymentHistory';
import PaymentHistoryEnterprise from './pages/payments/PaymentHistoryEnterprise';
import PaymentHistoryMutualiste from './pages/payments/PaymentHistoryMutualiste';
import ContributionCalls from './pages/payments/ContributionCalls';
import EnterprisePaymentConfig from './pages/payments/EnterprisePaymentConfig';
import PaymentUnified from './pages/payments/PaymentUnified';

function App() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  // Routes publiques
  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/register/complete" element={<RegisterComplete />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // Routes protégées
  return (
    <Layout>
      <Routes>
        {/* Routes communes */}
        <Route 
          path="/" 
          element={user.role === 'ADMIN' ? <AdminDashboard /> : <Dashboard />} 
        />
        <Route 
          path="/dashboard" 
          element={user.role === 'ADMIN' ? <AdminDashboard /> : <Dashboard />} 
        />
        <Route path="/profile" element={<Profile />} />
        
        {/* Routes mutualistes */}
        {user.role === 'MUTUALISTE' && (
          <>
            <Route path="/beneficiaries" element={<Beneficiaries />} />
            <Route path="/subscriptions" element={<Subscriptions />} />
            <Route path="/subscriptions/:id" element={<SubscriptionDetail />} />
            <Route path="/policies" element={<MyPolicies />} />
            <Route path="/contributions" element={<Contributions />} />
            <Route path="/offers" element={<Offers />} />
            <Route path="/payments/self-service" element={<PaymentSelfService />} />
            <Route path="/payments/history" element={<PaymentHistory />} />
            <Route path="/my-requests" element={<MyRequests />} />
          </>
        )}
        
        {/* Routes admin */}
        {user.role === 'ADMIN' && (
          <>
            <Route path="/users" element={<Users />} />
            <Route path="/admin/mutualistes" element={<AdminMutualistes />} />
            <Route path="/admin/mutualistes/liste" element={<AdminMutualistesList />} />
            <Route path="/admin/mutualistes/nouveau" element={<AdminMutualisteCreate />} />
            <Route path="/admin/mutualistes/:id" element={<AdminMutualisteDetail />} />
            <Route path="/admin/mutualistes/:id/modifier" element={<AdminMutualisteEdit />} />
            <Route path="/admin/subscriptions" element={<AdminSubscriptions />} />
            <Route path="/admin/policies" element={<AdminPolicies />} />
            <Route path="/admin/requests" element={<AdminRequests />} />
            <Route path="/admin/cron-jobs" element={<AdminCronJobs />} />
            <Route path="/admin/offers" element={<Offers admin />} />
            <Route path="/admin/parametrage" element={<AdminParametrage />} />
            <Route path="/admin/parametrage/situation-matrimoniale" element={<AdminSituationMatrimoniale />} />
            <Route path="/admin/parametrage/liens-parente" element={<AdminLiensParente />} />
            <Route path="/admin/parametrage/fonctions" element={<AdminFonctions />} />
            <Route path="/admin/parametrage/statuts" element={<AdminStatuts />} />
            <Route path="/admin/parametrage/entreprises" element={<AdminEnterprise />} />
            <Route path="/admin/parametrage/request-types" element={<AdminRequestTypes />} />
            <Route path="/admin/parametrage/reasons" element={<AdminReasons />} />
            <Route path="/admin/parametrage/reprise-donnees" element={<AdminDataMigration />} />
            <Route path="/admin/payments" element={<AdminPayments />} />
            <Route path="/admin/payments/mutualistes" element={<AdminPaymentsMutualistes />} />
            <Route path="/admin/payments/entreprises" element={<AdminPaymentsEntreprises />} />
            <Route path="/payments/third-party" element={<PaymentThirdParty />} />
            <Route path="/payments/bulk" element={<PaymentBulk />} />
            <Route path="/payments/history" element={<PaymentHistory />} />
            <Route path="/payments/history-entreprise" element={<PaymentHistoryEnterprise />} />
            <Route path="/payments/history-mutualiste" element={<PaymentHistoryMutualiste />} />
            <Route path="/payments/unified" element={<PaymentUnified />} />
            <Route path="/contribution-calls" element={<ContributionCalls />} />
            <Route path="/enterprise-payment-config" element={<EnterprisePaymentConfig />} />
          </>
        )}
        
        {/* Routes agent et superviseur */}
        {(user.role === 'AGENT' || user.role === 'SUPERVISEUR') && (
          <>
            <Route path="/offers" element={<Offers />} />
            <Route path="/admin/mutualistes" element={<AdminMutualistes />} />
            <Route path="/admin/mutualistes/liste" element={<AdminMutualistesList />} />
            <Route path="/admin/mutualistes/nouveau" element={<AdminMutualisteCreate />} />
            <Route path="/admin/mutualistes/:id" element={<AdminMutualisteDetail />} />
            <Route path="/admin/mutualistes/:id/modifier" element={<AdminMutualisteEdit />} />
            <Route path="/admin/subscriptions" element={<AdminSubscriptions />} />
            <Route path="/admin/policies" element={<AdminPolicies />} />
            <Route path="/admin/requests" element={<AdminRequests />} />
            <Route path="/admin/payments" element={<AdminPayments />} />
            <Route path="/admin/payments/mutualistes" element={<AdminPaymentsMutualistes />} />
            <Route path="/admin/payments/entreprises" element={<AdminPaymentsEntreprises />} />
            <Route path="/payments/third-party" element={<PaymentThirdParty />} />
            <Route path="/payments/bulk" element={<PaymentBulk />} />
            <Route path="/payments/history" element={<PaymentHistory />} />
            <Route path="/payments/history-entreprise" element={<PaymentHistoryEnterprise />} />
            <Route path="/payments/history-mutualiste" element={<PaymentHistoryMutualiste />} />
            <Route path="/payments/unified" element={<PaymentUnified />} />
            <Route path="/contribution-calls" element={<ContributionCalls />} />
            <Route path="/enterprise-payment-config" element={<EnterprisePaymentConfig />} />
          </>
        )}
        
        {/* Redirection par défaut */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

export default App;
